import React from 'react'
import { FormikErrors, FormikTouched } from 'formik'
import { Stack, HStack, Text } from '@chakra-ui/react'
import { LabeledSelect, LabeledInput } from '@apex/component-library'
import { TaxWithholdingTypes } from '@Types'
import { useFormatNumber, numberInputFormatter } from '../../utils/index'
import { IraDistributionFormikValues } from './IraDistributionForm'

export const isValidWithholdingType = (withholdingType?: TaxWithholdingTypes) => {
  return withholdingType === 'FIXED' || withholdingType === 'PERCENTAGE'
}

export interface TaxWithholdingFieldsFormikValues {
  federalTaxWithholdingType?: TaxWithholdingTypes
  federalTaxWithholdingAmountDisplay?: string
  federalTaxWithholdingAmountRaw?: number
  stateTaxWithholdingType?: TaxWithholdingTypes
  stateTaxWithholdingAmountDisplay?: string
  stateTaxWithholdingAmountRaw?: number
}

export interface TaxWithholdingFieldsProps {
  formikValues: TaxWithholdingFieldsFormikValues | IraDistributionFormikValues
  formikBlur: (e: React.FocusEvent<any>) => void
  formikTouched: FormikTouched<TaxWithholdingFieldsFormikValues>
  setFieldValue: (field: string, value: any, shouldValidate?: boolean | undefined) => void
  setTouched: (fields: object, validate: boolean) => void
  errors: FormikErrors<TaxWithholdingFieldsFormikValues>
  federalTaxWithholdingEnabled?: boolean
  stateTaxWithholdingEnabled?: boolean
  formikSetValues: (values: React.SetStateAction<any>, shouldValidate?: boolean | undefined) => void
}

export const TaxWithholdingFields = ({
  formikValues,
  formikBlur,
  setFieldValue,
  formikTouched,
  errors,
  federalTaxWithholdingEnabled,
  stateTaxWithholdingEnabled,
  formikSetValues
}: TaxWithholdingFieldsProps) => {
  const {
    federalTaxWithholdingType,
    federalTaxWithholdingAmountDisplay,
    stateTaxWithholdingType,
    stateTaxWithholdingAmountDisplay
  } = formikValues

  const numberFormatter = useFormatNumber()

  return (
    <Stack spacing={4}>
      {(federalTaxWithholdingEnabled || stateTaxWithholdingEnabled) && (
        <Text as='h6' paddingBottom={4}>
          Percentage amounts have a maximum of 100%. Express fixed amounts in dollars.
        </Text>
      )}
      {federalTaxWithholdingEnabled && (
        <HStack spacing={8}>
          <LabeledSelect
            label='Federal Tax Withholding Type'
            name='federalTaxWithholdingType'
            onBlur={formikBlur}
            value={federalTaxWithholdingType as string}
            onChange={(e) => {
              formikSetValues({
                ...formikValues,
                federalTaxWithholdingType: e.target.value,
                federalTaxWithholdingAmountRaw: 0,
                federalTaxWithholdingAmountDisplay: ''
              })
            }}
            isInvalid={formikTouched.federalTaxWithholdingType && !!errors.federalTaxWithholdingType}
            errorMessage={errors.federalTaxWithholdingType}
          >
            <option value=''>None</option>
            <option value={'FIXED'}>Fixed</option>
            <option value={'PERCENTAGE'}>Percentage</option>
          </LabeledSelect>
          <LabeledInput
            label='Federal Tax Withholding Amount'
            name='federalTaxWithholdingAmountRaw'
            value={federalTaxWithholdingAmountDisplay}
            onBlur={formikBlur}
            onChange={(e) => {
              setFieldValue(
                'federalTaxWithholdingAmountDisplay',
                numberInputFormatter(e.target.value, numberFormatter).display,
                true
              )
              setFieldValue(
                'federalTaxWithholdingAmountRaw',
                numberInputFormatter(e.target.value, numberFormatter).raw,
                true
              )
            }}
            errorMessage={errors?.federalTaxWithholdingAmountRaw}
            isInvalid={formikTouched.federalTaxWithholdingAmountRaw && !!errors?.federalTaxWithholdingAmountRaw}
            isDisabled={!isValidWithholdingType(federalTaxWithholdingType)}
            iconRight={federalTaxWithholdingType === 'PERCENTAGE' ? '%' : null}
            iconLeft={federalTaxWithholdingType === 'FIXED' ? '$' : null}
          />
        </HStack>
      )}
      {stateTaxWithholdingEnabled && (
        <HStack spacing={8}>
          <LabeledSelect
            label='State Tax Withholding Type'
            name='stateTaxWithholdingType'
            onBlur={formikBlur}
            value={stateTaxWithholdingType as string}
            onChange={(e) => {
              formikSetValues({
                ...formikValues,
                stateTaxWithholdingType: e.target.value,
                stateTaxWithholdingAmountRaw: 0,
                stateTaxWithholdingAmountDisplay: ''
              })
            }}
            isInvalid={formikTouched.stateTaxWithholdingType && !!errors.stateTaxWithholdingType}
            errorMessage={errors.stateTaxWithholdingType}
          >
            <option value=''>None</option>
            <option value={'FIXED'}>Fixed</option>
            <option value={'PERCENTAGE'}>Percentage</option>
          </LabeledSelect>
          <LabeledInput
            label='State Tax Withholding Amount'
            name='stateTaxWithholdingAmountRaw'
            value={stateTaxWithholdingAmountDisplay}
            onBlur={formikBlur}
            onChange={(e) => {
              setFieldValue(
                'stateTaxWithholdingAmountDisplay',
                numberInputFormatter(e.target.value, numberFormatter).display,
                true
              )
              setFieldValue(
                'stateTaxWithholdingAmountRaw',
                numberInputFormatter(e.target.value, numberFormatter).raw,
                true
              )
            }}
            errorMessage={errors?.stateTaxWithholdingAmountRaw}
            isInvalid={formikTouched.stateTaxWithholdingAmountRaw && !!errors?.stateTaxWithholdingAmountRaw}
            isDisabled={!isValidWithholdingType(stateTaxWithholdingType)}
            iconRight={stateTaxWithholdingType === 'PERCENTAGE' ? '%' : null}
            iconLeft={stateTaxWithholdingType === 'FIXED' ? '$' : null}
          />
        </HStack>
      )}
    </Stack>
  )
}
